import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Key, ArrowLeft, Clock, Shield, CheckCircle, RefreshCw,
  Share2, Download, Smartphone
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function LlaveDigital() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [qrCode, setQrCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [accessHistory, setAccessHistory] = useState([
    { id: '1', date: '2026-03-18', time: '08:30', location: 'Caseta Principal', type: 'entry' },
    { id: '2', date: '2026-03-17', time: '19:45', location: 'Caseta Principal', type: 'exit' },
  ]);

  useEffect(() => {
    // Generate random QR code string
    const generateQR = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let result = '';
      for (let i = 0; i < 20; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };
    
    setQrCode(generateQR());
    
    // Timer for QR refresh
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setQrCode(generateQR());
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 20; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setQrCode(result);
    setTimeLeft(60);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mi Código de Acceso Vecindus',
          text: `Código de acceso para ${user?.name}: ${qrCode}`,
        });
      } catch {
        alert('Código copiado al portapapeles');
      }
    } else {
      alert('Código copiado al portapapeles');
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Llave Digital</h1>
              <p className="text-xs text-gray-500">Acceso 24/7</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-md mx-auto">
        {/* Warning */}
        <div className="bg-[#36CCD8]/10 border border-[#36CCD8]/30 rounded-2xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#36CCD8] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#2D3748]">
              Este código QR es personal e intransferible. No lo compartas con nadie.
            </p>
          </div>
        </div>

        {/* QR Card */}
        <div className="bg-[var(--v-surface-1)] rounded-3xl border border-[var(--v-border-light)] shadow-lg p-8 mb-6">
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-[#2D3748]">Código de Acceso</h2>
            <p className="text-sm text-gray-500">{user?.name || 'Residente'} • {user?.email || 'info@vecindus.com'}</p>
          </div>
          
          {/* QR Placeholder */}
          <div className="bg-gray-900 rounded-2xl p-8 mb-6 aspect-square flex items-center justify-center relative overflow-hidden">
            <div className="text-center">
              <div className="text-white font-mono text-xs mb-4 break-all px-4">{qrCode}</div>
              <div className="w-48 h-48 bg-[var(--v-surface-1)] rounded-xl p-4 mx-auto">
                <div className="w-full h-full bg-gray-900 rounded-lg flex items-center justify-center">
                  <Key className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>
            
            {/* Timer ring */}
            <div className="absolute top-4 right-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
                timeLeft < 10 ? 'bg-red-500 text-white' : 'bg-white/20 text-white'
              }`}>
                {timeLeft}
              </div>
            </div>
          </div>
          
          <p className="text-center text-sm text-gray-500 mb-4">
            El código se actualiza automáticamente en {timeLeft} segundos
          </p>
          
          <div className="flex gap-3">
            <button 
              onClick={handleRefresh}
              className="flex-1 py-3 bg-[var(--v-surface-2)] text-[#2D3748] font-bold rounded-xl hover:bg-gray-200 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Actualizar
            </button>
            <button 
              onClick={handleShare}
              className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" /> Compartir
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
          <h3 className="font-bold text-[#2D3748] mb-4">¿Cómo usar?</h3>
          
          <ol className="space-y-3 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#36CCD8]/10 text-[#36CCD8] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <span>Acércate a la caseta de vigilancia</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#36CCD8]/10 text-[#36CCD8] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <span>Muestra tu código QR al vigilante</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#36CCD8]/10 text-[#36CCD8] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <span>El vigilante escaneará el código</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#36CCD8]/10 text-[#36CCD8] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
              <span>¡Acceso permitido!</span>
            </li>
          </ol>
        </div>

        {/* History */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6">
          <h3 className="font-bold text-[#2D3748] mb-4">Historial de Accesos</h3>
          
          <div className="space-y-3">
            {accessHistory.map(access => (
              <div key={access.id} className="flex items-center justify-between p-3 bg-[var(--v-surface-2)] rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    access.type === 'entry' ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    <CheckCircle className={`w-5 h-5 ${access.type === 'entry' ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-[#2D3748]">{access.type === 'entry' ? 'Entrada' : 'Salida'}</p>
                    <p className="text-xs text-gray-500">{access.location}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#2D3748]">{access.time}</p>
                  <p className="text-xs text-gray-500">{access.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
